import { Link, useSearchParams } from "react-router";
import { DollarSign, ListTodo } from "lucide-react";
import { useCrmToday } from "@/hooks/use-crm-today";
import { ActionQueue, ActionQueueSkeleton } from "@/components/crm/action-queue";
import { TaskOverview } from "@/components/crm/task-overview";
import { SuggestedTasks } from "@/components/crm/suggested-tasks";
import { ActionDecisionSummary } from "@/components/crm/action-decision-summary";
import { ActionEffectivenessSummary } from "@/components/crm/action-effectiveness-summary";
import { ActionExecutionQueue, type QueueView } from "@/components/crm/action-execution-queue";
import { ActionOutcomeLeaderboard } from "@/components/crm/action-outcome-leaderboard";
import { ActionTelemetrySnapshot } from "@/components/crm/action-telemetry-snapshot";
import { ActionContractRegistry } from "@/components/crm/action-contract-registry";
import { EventActionReviewBridge } from "@/components/crm/event-action-review-bridge";
import { useCrmActionContracts } from "@/hooks/use-crm-action-contracts";
import { useCrmEventActionContracts } from "@/hooks/use-crm-event-action-contracts";

type ActiveTab = "deals" | "tasks";

const QUEUE_VIEWS = new Set<QueueView>(["all", "ready", "outcome_backed", "materializable", "manual_only", "review", "bridge_blocked", "blocked"]);

function toQueueView(value: string | null): QueueView {
  return value && QUEUE_VIEWS.has(value as QueueView) ? value as QueueView : "all";
}

function toActiveTab(value: string | null): ActiveTab {
  return value === "tasks" ? "tasks" : "deals";
}

function prettyActionLabel(value: string | null | undefined): string {
  if (!value || value === "unknown") {
    return "Unknown action";
  }

  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function CrmTodayPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useCrmToday();
  const { data: actionContractsData } = useCrmActionContracts();
  const { data: eventActionContractsData } = useCrmEventActionContracts();

  const totalDeals = data
    ? data.closingToday.length + data.followUp.length + data.newLeads.length + data.overdue.length
    : 0;
  const contractRef = searchParams.get("contractRef") ?? "all";
  const signalFamilyRef = searchParams.get("signalFamily") ?? "all";
  const sourceEventTypeRef = searchParams.get("sourceEventType") ?? "all";
  const queueView = toQueueView(searchParams.get("queue"));
  const activeTab = toActiveTab(searchParams.get("tab"));
  const activeContractRef = contractRef === "all" ? undefined : contractRef;
  const activeSignalFamily = signalFamilyRef === "all" ? undefined : signalFamilyRef;
  const activeSourceEventType = sourceEventTypeRef === "all" ? undefined : sourceEventTypeRef;
  const actionContracts = actionContractsData?.contracts ?? [];
  const eventActionContracts = eventActionContractsData?.contracts ?? [];
  const signalFamilies = eventActionContractsData?.summary.signalFamilies ?? [];
  const sourceEventOptionMap = new Map(
    eventActionContracts
      .filter((contract) => !activeSignalFamily || contract.signalFamily === activeSignalFamily)
      .map((contract) => [contract.eventType, { eventType: contract.eventType }]),
  );
  if (activeSourceEventType && !sourceEventOptionMap.has(activeSourceEventType)) {
    sourceEventOptionMap.set(activeSourceEventType, { eventType: activeSourceEventType });
  }
  const sourceEventOptions = Array.from(sourceEventOptionMap.values())
    .sort((a, b) => prettyActionLabel(a.eventType).localeCompare(prettyActionLabel(b.eventType)));

  const updateSearchParam = (key: "contractRef" | "queue" | "tab" | "signalFamily" | "sourceEventType", value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value === "all" || (key === "tab" && value === "deals")) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      if (key === "signalFamily") {
        next.delete("sourceEventType");
      }
      return next;
    });
  };

  const updateSignalScope = (signalFamily: string, sourceEventType?: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("signalFamily", signalFamily);
      if (sourceEventType) {
        next.set("sourceEventType", sourceEventType);
      } else {
        next.delete("sourceEventType");
      }
      return next;
    });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-foreground)]">
              Here&apos;s Your Money
            </h1>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
              Revenue-prioritized deals plus your real CRM task workload.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/crm/away-summary"
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            >
              What Happened While Away
            </Link>
          </div>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-6 border-b border-[var(--color-border)]">
        {([
          { key: "deals" as const, label: "Action Queue", Icon: DollarSign, count: totalDeals },
          { key: "tasks" as const, label: "Tasks", Icon: ListTodo },
        ]).map(({ key, label, Icon, count }) => (
          <button
            key={key}
            onClick={() => updateSearchParam("tab", key)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === key
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
            {count != null && count > 0 && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)] mb-4">
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      )}

      {/* Content */}
      {activeTab === "deals" ? (
        isLoading || !data ? (
          <ActionQueueSkeleton />
        ) : (
          <ActionQueue data={data} />
        )
      ) : (
        <div className="space-y-6">
          <TaskOverview />
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[var(--color-foreground)]">Action contract scope</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Apply reusable CRM action, signal family, and source event filters across the action loop.
                </p>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <select
                  value={contractRef}
                  onChange={(event) => updateSearchParam("contractRef", event.target.value)}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]"
                  aria-label="Filter CRM action loop by contract"
                >
                  <option value="all">All contracts</option>
                  {actionContracts.map((contract) => (
                    <option key={contract.ref} value={contract.ref}>
                      {prettyActionLabel(contract.ref)}
                    </option>
                  ))}
                </select>
                <select
                  value={signalFamilyRef}
                  onChange={(event) => updateSearchParam("signalFamily", event.target.value)}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]"
                  aria-label="Filter CRM queue by signal family"
                >
                  <option value="all">All signal families</option>
                  {signalFamilies.map((family) => (
                    <option key={family} value={family}>
                      {prettyActionLabel(family)}
                    </option>
                  ))}
                </select>
                <select
                  value={sourceEventTypeRef}
                  onChange={(event) => updateSearchParam("sourceEventType", event.target.value)}
                  className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]"
                  aria-label="Filter CRM queue by source event"
                >
                  <option value="all">All source events</option>
                  {sourceEventOptions.map((contract) => (
                    <option key={contract.eventType} value={contract.eventType}>
                      {prettyActionLabel(contract.eventType)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {(activeContractRef || activeSignalFamily || activeSourceEventType) && (
              <p className="mt-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
                Action loop scoped to {[
                  activeContractRef ? prettyActionLabel(activeContractRef) : null,
                  activeSignalFamily ? `${prettyActionLabel(activeSignalFamily)} family` : null,
                  activeSourceEventType ? prettyActionLabel(activeSourceEventType) : null,
                ].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <ActionTelemetrySnapshot
            contractRef={activeContractRef}
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
            onSignalFamilySelect={(signalFamily) => updateSignalScope(signalFamily)}
            onSourceEventTypeSelect={(sourceEventType, signalFamily) => updateSignalScope(signalFamily, sourceEventType)}
          />
          <ActionContractRegistry
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
            onSignalFamilySelect={(signalFamily) => updateSignalScope(signalFamily)}
            onSourceEventTypeSelect={(sourceEventType, signalFamily) => updateSignalScope(signalFamily, sourceEventType)}
          />
          <ActionDecisionSummary
            contractRef={activeContractRef}
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
          />
          <ActionEffectivenessSummary
            contractRef={activeContractRef}
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
          />
          <ActionOutcomeLeaderboard
            contractRef={activeContractRef}
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
          />
          <EventActionReviewBridge
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
            onQueued={() => updateSearchParam("queue", "ready")}
            onSignalFamilySelect={(signalFamily) => updateSignalScope(signalFamily)}
            onSourceEventTypeSelect={(sourceEventType, signalFamily) => updateSignalScope(signalFamily, sourceEventType)}
          />
          <ActionExecutionQueue
            contractRef={activeContractRef}
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
            queueView={queueView}
            onQueueViewChange={(nextQueueView) => updateSearchParam("queue", nextQueueView)}
          />
          <SuggestedTasks
            contractRef={activeContractRef}
            signalFamily={activeSignalFamily}
            sourceEventType={activeSourceEventType}
            onAccepted={() => updateSearchParam("queue", "ready")}
          />
        </div>
      )}
    </div>
  );
}
